/**
 * A gusty, mostly horizontal wind field. Seeded and deterministic in time.
 *
 *   wind(p, t) = dir(t) * speed(t)                          steady breeze + slow envelope + rare strong gusts
 *              + turbulence(t) * curl2(advected p, t)       divergence-free eddies blown through by the mean flow
 *              + small vertical noise
 *
 * `gust` exposes the envelope in 0..1 for atmosphere and the audio wind bed.
 */
import type { Vec3 } from '../types';
import { Simplex } from '../util/noise';
import { mulberry32, clamp01, smoothstep, lerp } from '../util/math';
import { PHYS } from './params';

const W = PHYS.wind;

export class WindField {
  /** unit prevailing direction in the XZ plane */
  dirX = 1;
  dirZ = 0;
  /** mean speed, m/s (before turbulence) */
  speed = 0;
  /** envelope 0..1 */
  gust = 0;
  /** the strong-gust envelope alone, m/s */
  strong = 0;
  /** seconds since the last strike; the world writes this so long silences get a nudge */
  silence = 0;
  /** the derived aerodynamic gain (set by the world; informational) */
  gain = 0;

  private readonly noise: Simplex;
  private readonly rng: () => number;
  private readonly psi0: number;
  private t = 0;
  private advX = 0;
  private advZ = 0;
  private nextGustAt: number;
  private gustStart = -1e9;
  private gustPeak = 0;
  private gustDur = 1;
  private gustDirX = 1;
  private gustDirZ = 0;

  constructor(seed: number) {
    this.noise = new Simplex(seed ^ 0x5bd1e995);
    this.rng = mulberry32(seed + 1013904223);
    // prevailing direction: mostly along X (side to side for the viewer), either way
    const side = this.rng() < 0.5 ? 0 : Math.PI;
    this.psi0 = side + (this.rng() - 0.5) * 1.0;
    this.nextGustAt = 6 + this.rng() * 12;
    this.update(0, 0);
  }

  /** advance the envelopes to simulation time t (dt is the step just taken) */
  update(t: number, dt: number): void {
    this.t = t;
    const n = this.noise;
    // prevailing direction veers slowly round the compass and wanders on top of that
    const veer = W.veerPeriod > 0 ? (2 * Math.PI * t) / W.veerPeriod : 0;
    const psi = this.psi0 + veer + W.dirWander * (1.6 * n.noise2(t / W.dirPeriod, 41.3) + 0.4 * n.noise2(t / 11, 5.2));
    const baseX = Math.cos(psi), baseZ = Math.sin(psi);
    // slow envelope 0..1 (noise2 rms ~0.44 so scale it up and clamp)
    const slow = clamp01(0.5 + 1.15 * n.noise2(t / W.slowPeriod, 11.7));
    // rare strong gusts, scheduled; each is an eddy with its own direction
    const starving = this.silence > W.silenceLimit;
    if (starving && this.nextGustAt - t > 6 && t - this.gustStart > this.gustDur) {
      // the piece has gone quiet: bring the next gust forward
      this.nextGustAt = t + 2 + 4 * this.rng();
    }
    if (t >= this.nextGustAt) {
      this.gustStart = t;
      this.gustPeak = lerp(starving ? W.strongPeakMin + 0.6 : W.strongPeakMin, W.strongPeakMax, this.rng());
      this.gustDur = lerp(W.strongDurationMin, W.strongDurationMax, this.rng());
      this.nextGustAt = t + this.gustDur + lerp(W.strongGapMin, W.strongGapMax, this.rng());
      const gpsi = this.psi0 + (this.rng() - 0.5) * W.gustDirSpread;
      this.gustDirX = Math.cos(gpsi);
      this.gustDirZ = Math.sin(gpsi);
    }
    const u = (t - this.gustStart) / this.gustDur;
    let strong = 0;
    if (u >= 0 && u <= 1) {
      strong = this.gustPeak * smoothstep(0, W.strongRise, u) * (1 - smoothstep(W.strongFallFrom, 1, u));
    }
    this.strong = strong;
    const base = W.breeze * (0.5 + slow) + W.slowBoost * slow * slow * slow;
    // blend directions by strength so the gust eddy takes over while it blows
    let dx = baseX * base + this.gustDirX * strong, dz = baseZ * base + this.gustDirZ * strong;
    const dl = Math.hypot(dx, dz);
    if (dl > 1e-6) { dx /= dl; dz /= dl; } else { dx = baseX; dz = baseZ; }
    this.dirX = dx;
    this.dirZ = dz;
    this.speed = Math.min(W.maxMeanSpeed, base + strong);
    this.gust = clamp01((this.speed - 0.3) / 2.2);
    // advect the turbulence field downstream (Taylor's frozen-turbulence hypothesis)
    this.advX += this.dirX * this.speed * dt;
    this.advZ += this.dirZ * this.speed * dt;
  }

  /** wind velocity at a world position, at the current time */
  sample(pos: Vec3, out: Vec3): Vec3 {
    const n = this.noise;
    const s = this.speed;
    const inv = 1 / W.turbScale;
    const px = (pos.x - this.advX) * inv;
    const pz = (pos.z - this.advZ) * inv + pos.y * inv * 0.35;
    const tt = this.t / W.turbTime;
    const [cx, cz] = n.curl2(px + 3.7, pz - 1.9, tt);
    const turb = W.turbulence * (s + 0.15) * W.curlNorm;
    out.x = this.dirX * s + cx * turb;
    out.z = this.dirZ * s + cz * turb;
    out.y = W.vertical * (s + 0.1) * n.noise3(px * 0.7 + 17.1, pos.y * inv, tt * 0.8);
    return out;
  }
}
